import { AlpineComponent } from "alpinejs";
import { Canvg } from "canvg";
import { jsPDF } from "jspdf";
import encodeQR from "qr";
import lsLogoSvg from "../../public/icons/ls-logo-dark.svg?raw";
import zigZagSvg from "../../public/icons/zig-zag-shape.svg?raw";
import { FeaturesTypeViewModel } from "../types/components/FeaturesTypeViewModel.ts";
import { ProductPdfVariantViewModel } from "../types/components/ProductPdfVariantViewModel.ts";
import { ProductPdfViewModel } from "../types/components/ProductPdfViewModel.ts";
import { ProductSpecificationGroupViewModel } from "../types/components/ProductSpecificationGroupViewModel.ts";
import { ProductSpecificationViewModel } from "../types/components/ProductSpecificationViewModel.ts";
import { BlockContainer } from "./pdf-generator/elements/blockContainer.ts";
import { BlockInlineContainer } from "./pdf-generator/elements/blockInlineContainer.ts";
import { Empty } from "./pdf-generator/elements/empty.ts";
import { FixedContainer } from "./pdf-generator/elements/fixedContainer.ts";
import { FlexContainer } from "./pdf-generator/elements/flexContainer.ts";
import { GridContainer } from "./pdf-generator/elements/gridContainer.ts";
import { ImagePdf } from "./pdf-generator/elements/imagePdf.ts";
import { Line } from "./pdf-generator/elements/line.ts";
import { PageContainer } from "./pdf-generator/elements/pageContainer.ts";
import { RichText2D } from "./pdf-generator/elements/richText2d.ts";
import { Svg } from "./pdf-generator/elements/svg.ts";
import { Text2D } from "./pdf-generator/elements/text2d.ts";
import { Box } from "./pdf-generator/layout/box.ts";
import { Margin } from "./pdf-generator/layout/margin.ts";
import { Position } from "./pdf-generator/layout/position.ts";
import { Size } from "./pdf-generator/layout/size.ts";
import { PdfMath } from "./pdf-generator/shared/pdfMath.ts";

// For the UMD build: window.jspdf.jsPDF

type PdfProductSheetComponent = AlpineComponent<{
	busy: boolean;
	generate: () => Promise<void>;
}>;

const pdfProductSheet = (
	product: ProductPdfViewModel,
	isInteriorTextiles: boolean,
	currentCulture: string
): PdfProductSheetComponent => {
	const fontGeneral = "HelveticaNeue";
	const fontChinese = "NotoSansSC";
	const fontKorean = "NotoSansKR";

	let fontFamily = "";
	if (currentCulture == "zh-Hans") {
		fontFamily = fontChinese;
	} else if (currentCulture == "ko-KR") {
		fontFamily = fontKorean;
	} else {
		fontFamily = fontGeneral;
	}

	const primaryColor = "#1a1919";
	const secondaryColor = "#646363";

	const headerSize = 16;
	const subHeaderSize = 14;
	const textSize = 11.34;
	const smallTextSize = 8.5;
	const headerSizeMm = PdfMath.toMm(16);
	const subHeaderSizeMm = PdfMath.toMm(14);
	const textSizeMm = PdfMath.toMm(textSize);
	const smallTextSizeMm = PdfMath.toMm(smallTextSize);

	const fontWeightNormal = "normal";
	const fontWeightBold = "bold";

	const _processSpecificationItem = (anySpecification: ProductSpecificationViewModel, grid: GridContainer) => {
		const specItem = anySpecification as ProductSpecificationViewModel;
		const specificationKey = new Text2D(
			new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
			"specification-key",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			specItem.name.toUpperCase(),
			primaryColor
		);
		grid.addElement(specificationKey);
		if (specItem.typeViewModel & FeaturesTypeViewModel.Image) {
			const designValue = new ImagePdf(new Box(), "specification-image", specItem.value);
			grid.addElement(designValue);
		} else {
			const designValue = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
				"specification-value",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				specItem.value,
				primaryColor
			);
			grid.addElement(designValue);
		}
	};
	const toSvgCanvas = async (svgString: string) => {
		const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
		const svgElement = doc.documentElement;

		let width: number | null;
		let height: number | null;

		const widthAttribute = svgElement.getAttribute("width");
		const heightAttribute = svgElement.getAttribute("height");
		const viewBoxAttribute = svgElement.getAttribute("viewBox");

		const toPx = (value: string | null) => {
			if (!value) {
				return null;
			}
			// crude: assume px if unitless; strip 'px'
			const m = /^([\d.]+)(px)?$/i.exec(value.trim());
			return m ? Number.parseFloat(m[1]) : null;
		};

		width = toPx(widthAttribute);
		height = toPx(heightAttribute);

		if ((!width || !height) && viewBoxAttribute) {
			const [, , viewBoxWidth, viewBoxHeight] = viewBoxAttribute.split(/\s+/).map(Number);
			width = width ?? viewBoxWidth;
			height = height ?? viewBoxHeight;
		}

		if (!width || !height) {
			width = 512;
			height = 512;
		}

		const canvas = document.createElement("canvas");
		canvas.width = Math.ceil(width);
		canvas.height = Math.ceil(height);

		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Failed to get 2D context");
		}

		const canvg = Canvg.fromString(context, svgString, {
			ignoreMouse: true,
			ignoreAnimation: true,
			ignoreDimensions: false,
			scaleWidth: canvas.width,
			scaleHeight: canvas.height,
		});

		await canvg.render();
		return canvas;
	};
	const loadFontFromUrl = async (doc: jsPDF, url: string, fontName: string, style: string = fontWeightNormal) => {
		// 1. Fetch as ArrayBuffer
		const res = await fetch(url);
		if (!res.ok) {
			throw new Error(`Failed to load font from ${url}: ${res.status} ${res.statusText}`);
		}
		const buffer = await res.arrayBuffer();

		// 2. Convert to base64
		const uint8 = new Uint8Array(buffer);
		let binary = "";
		for (const element of uint8) {
			binary += String.fromCodePoint(element);
		}
		const base64 = btoa(binary);

		// 3. Register with jsPDF
		const fileName = `${fontName}-${style}.ttf`; // virtual name in VFS
		doc.addFileToVFS(fileName, base64);
		doc.addFont(fileName, fontName, style);
	};
	const addItMainPage = async (pdfDocument: jsPDF) => {
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};
		const root = new BlockContainer(
			new Box(new Position(), new Size(page.width, page.height), new Margin(7, 7, 7, 0)),
			"root"
		);
		const rootSize = root.box.getBoundingBox();
		const mainImage = new ImagePdf(
			new Box(new Position(), new Size(rootSize.width, rootSize.height), new Margin(0, 0, 0, 25)),
			"main-image",
			product.frontImage
		);

		const mainImageWaveContainer = new FixedContainer(new Box(new Position(7, 7)), "main-image-wave-container");
		const waveSvgIcon = await toSvgCanvas(zigZagSvg);
		const waveSvg = new Svg(new Box(new Position(0, 0), new Size(49, 0)), "wave-svg", waveSvgIcon);
		mainImageWaveContainer.addElement(waveSvg);

		const mainFooterFixed = new FixedContainer(new Box(new Position(11, page.height - 18)), "main-footer-root");
		const mainFooterBlockContainer = new BlockContainer(
			new Box(new Position(), new Size(100, 20)),
			"main-footer-block-container"
		);
		const mainFooterHeader = new Text2D(
			new Box(new Position(), new Size(0, subHeaderSizeMm)),
			"header",
			fontFamily,
			fontWeightBold,
			subHeaderSize,
			product.name,
			primaryColor
		);
		const mainFooterSubHeader = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm), new Margin(0, 2, 0, 0)),
			"sub-header",
			fontFamily,
			fontWeightBold,
			textSize,
			product.category?.toUpperCase(),
			secondaryColor
		);
		mainFooterBlockContainer.addElement(mainFooterHeader);
		mainFooterBlockContainer.addElement(mainFooterSubHeader);

		mainFooterFixed.addElement(mainFooterBlockContainer);

		root.addElement(mainImage);
		root.addElement(mainImageWaveContainer);
		root.addElement(mainFooterFixed);

		await root.calculate(pdfDocument);

		await root.draw(pdfDocument);

		await addFooterLogo(pdfDocument);
	};
	const addItVariantsPage = async (pdfDocument: jsPDF) => {
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};

		const root = new PageContainer(
			new Box(new Position(), new Size(page.width, page.height), new Margin(7, 7, 7, 15)),
			"root"
		);
		root.addFooter = addFooterLogo;

		const chunk = (array: ProductPdfVariantViewModel[], size: number) => {
			if (!Number.isInteger(size) || size <= 0) {
				throw new RangeError("size must be a positive integer");
			}

			const result = [];
			for (let i = 0; i < array.length; i += size) {
				result.push(array.slice(i, i + size));
			}
			return result;
		};

		const variantsBatch = chunk(product.variants, 4);
		for (const variantBatch of variantsBatch) {
			const gridContainer = new GridContainer(
				new Box(new Position(), new Size(), new Margin(0, 0, 0, 6)),
				"variants-grid",
				4,
				8
			);
			for (const variant of variantBatch) {
				const container = new BlockContainer(new Box(), "variant-container");

				const imageContainer = new BlockContainer(new Box(), "variant-image-container");
				const variantImage = new ImagePdf(
					new Box(new Position(), new Size(0, 0), new Margin(0, 0, 0, 1)),
					"variant-image",
					variant.image
				);
				imageContainer.addElement(variantImage);

				const nameContainer = new BlockContainer(new Box(), "variant-name-container");
				const name = new Text2D(
					new Box(),
					"variant-name",
					fontFamily,
					fontWeightNormal,
					textSize,
					variant.name,
					primaryColor
				);
				nameContainer.addElement(name);

				container.addElement(imageContainer);
				container.addElement(nameContainer);

				gridContainer.addElement(container);
			}

			root.addElement(gridContainer);
		}

		await root.calculate(pdfDocument);
		await root.draw(pdfDocument);
		await addFooterLogo(pdfDocument);
	};
	const addFooterLogo = async (pdfDocument: jsPDF) => {
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};
		const root = new FixedContainer(new Box(new Position(page.width - 50, page.height - 15)), "footer-fixed-container");
		const logoCanvas = await toSvgCanvas(lsLogoSvg);
		const footerLogo = new Svg(new Box(new Position(), new Size(43, 0)), "footer-logo", logoCanvas);

		root.addElement(footerLogo);
		await root.calculate(pdfDocument);

		await root.draw(pdfDocument);
	};
	const interiorTextilesLayout = async (pdfDocument: jsPDF) => {
		pdfDocument.setProperties({
			title: `${product.name} productsheet`,
			keywords: `LudvigSvensson, productsheet`,
			author: "Svensson Interior Textiles",
		});
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};
		await addItMainPage(pdfDocument);
		pdfDocument.addPage();
		const root = new BlockContainer(
			new Box(new Position(), new Size(page.width, page.height), new Margin(7, 10, 7, 6)),
			"root"
		);

		const header = new Text2D(
			new Box(new Position(), new Size(0, headerSizeMm)),
			"header",
			fontFamily,
			fontWeightBold,
			headerSize,
			product.name,
			primaryColor
		);
		const subHeader = new Text2D(
			new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 1, 0, 0)),
			"sub-header",
			fontFamily,
			fontWeightBold,
			smallTextSize,
			product.category?.toUpperCase(),
			secondaryColor
		);
		const topLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 2, 0, 0)),
			"top-line",
			secondaryColor
		);
		const gridContainer = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 3, 0, 3)),
			"specification-grid",
			2,
			8
		);
		const leftText = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm)),
			"specification-header",
			fontFamily,
			fontWeightBold,
			textSize,
			product.specificationsHeader.toUpperCase(),
			primaryColor
		);
		const rightText = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm)),
			"description-header",
			fontFamily,
			fontWeightBold,
			textSize,
			product.descriptionHeader.toUpperCase(),
			primaryColor
		);
		gridContainer.addElement(leftText);
		gridContainer.addElement(rightText);
		const bottomLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 0, 0, 0)),
			"bottom-line",
			secondaryColor
		);

		const gridDiv2 = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 1, 0, 0)),
			"specification-description-grid",
			2,
			8
		);
		const gridDiv2InnerLeft = new GridContainer(new Box(), "specification-grid", 2, 4, 1);
		for (const specification of product.specifications.specifications) {
			if (specification.specType == "value") {
				const spec = specification as ProductSpecificationViewModel;
				_processSpecificationItem(spec, gridDiv2InnerLeft);
			} else if (specification.specType == "group") {
				const specGroup = specification as ProductSpecificationGroupViewModel;
				const groupName = new Text2D(
					new Box(new Position(), new Size(0, textSizeMm)),
					"group-name",
					fontFamily,
					fontWeightBold,
					textSize,
					specGroup.name,
					primaryColor
				);
				groupName.colSpan = 2;
				gridDiv2InnerLeft.addElement(groupName);
				for (const specification1 of specGroup.specifications) {
					_processSpecificationItem(specification1, gridDiv2InnerLeft);
				}
			}
		}

		const gridDiv2InnerRight = new BlockContainer(new Box(), "description-container");
		const description = new RichText2D(
			new Box(new Position(), new Size(0, smallTextSizeMm)),
			"description",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			product.description,
			primaryColor
		);
		gridDiv2InnerRight.addElement(description);

		gridDiv2.addElement(gridDiv2InnerLeft);
		gridDiv2.addElement(gridDiv2InnerRight);

		const durabilityContainer = new BlockContainer(
			new Box(new Position(), new Size(), new Margin(0, 8, 0, 0)),
			"durability-container"
		);
		const durabilityTopLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 2, 0, 3)),
			"durability-top-line",
			secondaryColor
		);
		const durabilitySewingHeaderGridContainer = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 0, 0, 0)),
			"specification-grid",
			2,
			8
		);
		const durabilityHeader = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm)),
			"durability-header",
			fontFamily,
			fontWeightBold,
			textSize,
			product.durabilityHeader.toUpperCase(),
			primaryColor
		);
		const sewingHeader = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm)),
			"durability-header",
			fontFamily,
			fontWeightBold,
			textSize,
			product.sewingHeader.toUpperCase(),
			primaryColor
		);
		const durabilityBottomLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 3, 0, 0)),
			"durability-bottom-line",
			secondaryColor
		);
		durabilitySewingHeaderGridContainer.addElement(durabilityHeader);
		durabilitySewingHeaderGridContainer.addElement(sewingHeader);

		durabilityContainer.addElement(durabilityTopLine);
		durabilityContainer.addElement(durabilitySewingHeaderGridContainer);
		durabilityContainer.addElement(durabilityBottomLine);

		const durabilitySewingGrid = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 1, 0, 0)),
			"durability-sewing-grid",
			2,
			8
		);
		const durabilityGridLeft = new GridContainer(new Box(), "durability-left-grid", 2, 8, 1);
		for (const durabilityItem of product.durability.items) {
			const durabilityKey = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
				"durability-key",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				durabilityItem.name.toUpperCase(),
				primaryColor
			);
			const durabilityValue = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm)),
				"durability-value",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				durabilityItem.value,
				primaryColor
			);
			durabilityGridLeft.addElement(durabilityKey);
			durabilityGridLeft.addElement(durabilityValue);
		}
		const durabilitySewingGridRight = new GridContainer(new Box(), "durability-right-grid", 2, 8, 1);
		for (const sewingItem of product.sewing.items) {
			const sewingKey = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
				"sewing-key",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				sewingItem.name.toUpperCase(),
				primaryColor
			);
			const sewingValue = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm)),
				"sewing-value",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				sewingItem.value,
				primaryColor
			);
			durabilitySewingGridRight.addElement(sewingKey);
			durabilitySewingGridRight.addElement(sewingValue);
		}
		durabilitySewingGrid.addElement(durabilityGridLeft);
		durabilitySewingGrid.addElement(durabilitySewingGridRight);

		const imageFlexContainer = new FlexContainer(
			new Box(new Position(), new Size(0, 0), new Margin(0, 2, 0, 0)),
			"image-block-inline-container",
			4,
			"center"
		);
		for (const certificate of product.certificates) {
			if (!certificate.image) {
				continue;
			}
			const certificateImage = new ImagePdf(
				new Box(new Position(), new Size(6, 0)),
				"certificate-image",
				certificate.image
			);
			imageFlexContainer.addElement(certificateImage);
		}

		const footerContainer = new FixedContainer(
			new Box(new Position(7, page.height - 6 - 23 - 5)),
			"footer-fixed-container"
		);
		const footerLine = new Line(
			new Box(new Position(), new Size(page.width - 7 - 7, 0.5), new Margin(0, 0, 0, 0)),
			"footer-line",
			secondaryColor
		);
		footerContainer.addElement(footerLine);

		const svgElement = await toSvgCanvas(encodeQR(globalThis.location.href, "svg"));
		const qrFixedContainer = new FixedContainer(new Box(new Position(10, page.height - 6 - 23)), "qr-fixed-container");
		const qrImage = new Svg(new Box(new Position(), new Size(23, 23)), "qr-svg", svgElement);
		qrFixedContainer.addElement(qrImage);

		const footerTextContainer = new FixedContainer(
			new Box(new Position(10 + 23 + 10, page.height - 4 - 23)),
			"footer-text-fixed-container"
		);
		const footerText = new Text2D(
			new Box(new Position(), new Size(100, smallTextSizeMm)),
			"footer-text",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			product.footer,
			primaryColor
		);
		footerTextContainer.addElement(footerText);

		root.addElement(header);
		root.addElement(subHeader);
		root.addElement(topLine);
		root.addElement(gridContainer);
		root.addElement(bottomLine);
		root.addElement(gridDiv2);
		root.addElement(durabilityContainer);
		root.addElement(durabilitySewingGrid);
		root.addElement(imageFlexContainer);
		root.addElement(qrFixedContainer);
		root.addElement(footerContainer);
		root.addElement(footerTextContainer);

		await root.calculate(pdfDocument);
		await root.draw(pdfDocument);
		await addFooterLogo(pdfDocument);

		pdfDocument.addPage();
		await addItVariantsPage(pdfDocument);
	};
	const climateScreensLayout = async (pdfDocument: jsPDF) => {
		pdfDocument.setProperties({
			title: `${product.name} productsheet`,
			keywords: `LudvigSvensson, productsheet`,
			author: "Svensson Climate Screens",
		});
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};
		const root = new PageContainer(
			new Box(new Position(), new Size(page.width, page.height), new Margin(7, 7, 7, 15)),
			"root"
		);
		root.addFooter = addFooterLogo;

		const headerContainer = new BlockInlineContainer(
			new Box(new Position(), new Size(), new Margin(0, 7, 0, 0)),
			"header-container"
		);
		const header = new Text2D(
			new Box(new Position(), new Size(130, PdfMath.toMm(17)), new Margin(0, 1.5, 0, 0)),
			"header",
			fontFamily,
			fontWeightBold,
			17,
			product.name.toUpperCase(),
			primaryColor
		);
		headerContainer.addElement(header);

		const headerLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 2.5, 0, 0)),
			"header-line",
			secondaryColor
		);
		const productInformationHeader = new Text2D(
			new Box(new Position(), new Size(0, textSizeMm), new Margin(0, 4, 0, 0)),
			"product-information-header",
			fontFamily,
			fontWeightBold,
			textSize,
			"product information".toUpperCase(),
			primaryColor
		);
		const headerLine2 = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 4, 0, 0)),
			"header-line",
			secondaryColor
		);

		const productInformationGrid = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 2, 0, 0)),
			"product-information-grid",
			2,
			4,
			2
		);
		for (const fact of product.quickFacts.facts) {
			if (fact.name) {
				const productInformationKey = new Text2D(
					new Box(new Position(), new Size(0, textSizeMm)),
					"product-information-key",
					fontFamily,
					fontWeightNormal,
					textSize,
					fact.name.toUpperCase(),
					primaryColor
				);
				productInformationGrid.addElement(productInformationKey);
			} else {
				productInformationGrid.addElement(new Empty());
			}
			if (fact.values) {
				const value = fact.values.map((v) => `${v.value}`).join(", ");
				const productInformationValue = new Text2D(
					new Box(new Position(), new Size(0, textSizeMm)),
					"product-information-value",
					fontFamily,
					fontWeightNormal,
					textSize,
					value,
					primaryColor
				);
				productInformationGrid.addElement(productInformationValue);
			}
		}

		const productInformationGridLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 5, 0, 0)),
			"product-information-grid-line",
			secondaryColor
		);

		const productPropertiesHeaderGrid = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 4, 0, 0)),
			"product-properties-header-grid",
			6,
			4
		);
		const addPropertyHeader = (header: string, colSpan = 1) => {
			if (header) {
				const productPropertiesHeader1 = new Text2D(
					new Box(new Position(), new Size(0, textSizeMm)),
					"product-properties-header-1",
					fontFamily,
					fontWeightBold,
					textSize,
					header.toUpperCase(),
					primaryColor
				);
				productPropertiesHeader1.colSpan = colSpan;
				productPropertiesHeaderGrid.addElement(productPropertiesHeader1);
			} else {
				productPropertiesHeaderGrid.addElement(new Empty());
			}
		};
		addPropertyHeader(product.propertiesHeader, 3);
		addPropertyHeader(product.valueHeader);
		addPropertyHeader(product.unitHeader);
		addPropertyHeader(product.testMethodsHeader);
		const productPropertiesGridLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 4, 0, 0)),
			"product-properties-grid-line",
			secondaryColor
		);

		const productPropertiesValuesGrid = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 1, 0, 0)),
			"product-properties-value-grid",
			6,
			4,
			0
		);

		const addPropertyValue = (value: string, colSpan = 1) => {
			if (value) {
				const productPropertiesValue1 = new Text2D(
					new Box(new Position(), new Size(0, textSizeMm), new Margin(0, 0, 0, 1)),
					"product-properties-value-1",
					fontFamily,
					fontWeightNormal,
					textSize,
					value,
					primaryColor
				);
				productPropertiesValue1.colSpan = colSpan;
				productPropertiesValuesGrid.addElement(productPropertiesValue1);
			} else {
				const empty = new Empty();
				empty.colSpan = colSpan;
				productPropertiesValuesGrid.addElement(empty);
			}
		};
		for (const specification of product.csProperties) {
			addPropertyValue(specification.name?.toUpperCase(), 3);
			addPropertyValue(specification.value);
			addPropertyValue(specification.unit);
			addPropertyValue(specification.testMethod);
		}

		const notesLine = new Line(
			new Box(new Position(), new Size(0, 0.5), new Margin(0, 2, 0, 1.5)),
			"notes-line",
			secondaryColor
		);

		root.addElement(headerContainer);
		root.addElement(headerLine);
		root.addElement(productInformationHeader);
		root.addElement(headerLine2);
		root.addElement(productInformationGrid);

		const propertiesBlockContainer = new BlockContainer(new Box(), "properties-block-container");
		propertiesBlockContainer.addElement(productInformationGridLine);
		propertiesBlockContainer.addElement(productPropertiesHeaderGrid);
		propertiesBlockContainer.addElement(productPropertiesGridLine);
		propertiesBlockContainer.addElement(productPropertiesValuesGrid);
		root.addElement(propertiesBlockContainer);
		root.addElement(notesLine);

		const addNote = (text: string) => {
			if (text) {
				const note = new Text2D(
					new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
					"note-1",
					fontFamily,
					fontWeightNormal,
					smallTextSize,
					text,
					primaryColor
				);
				root.addElement(note);
			}
		};

		addNote(product.noteWidthHeader);
		addNote(product.noteParHeader);
		addNote(product.noteUvLightHeader);

		const imageGridContainer = new GridContainer(
			new Box(new Position(), new Size(), new Margin(0, 2, 0, 0)),
			"image-grid-container",
			2,
			5
		);
		const mainImage = new ImagePdf(new Box(new Position(), new Size(0, 0)), "main-image", product.frontImage);
		imageGridContainer.addElement(mainImage);

		if (product.disclaimer) {
			const disclaimer = new Text2D(
				new Box(new Position(), new Size(0, smallTextSizeMm), new Margin(0, 0, 0, 1)),
				"note-4",
				fontFamily,
				fontWeightNormal,
				smallTextSize,
				product.disclaimer,
				primaryColor
			);
			disclaimer.maxWidth = 150;
			imageGridContainer.addElement(disclaimer);
		}

		root.addElement(imageGridContainer);
		await root.calculate(pdfDocument);
		await root.draw(pdfDocument);

		await addFooterLogo(pdfDocument);
	};

	return {
		busy: false,
		async generate() {
			const { jsPDF } = await import("jspdf");
			this.busy = true;
			try {
				const pdfDocument = new jsPDF({ unit: "mm", format: "a4", compress: true });

				if (fontFamily === fontChinese) {
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/NotoSansSC-Regular.ttf", "NotoSansSC", fontWeightNormal);
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/NotoSansSC-Bold.ttf", "NotoSansSC", fontWeightBold);
				} else if (fontFamily === fontKorean) {
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/NotoSansKR-Regular.ttf", "NotoSansKR", fontWeightNormal);
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/NotoSansKR-Bold.ttf", "NotoSansKR", fontWeightBold);
				} else {
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/HelveticaNeueRoman.ttf", "HelveticaNeue", fontWeightNormal);
					await loadFontFromUrl(pdfDocument, "/_esfn/fonts/HelveticaNeueBold.ttf", "HelveticaNeue", fontWeightBold);
				}

				if (isInteriorTextiles) {
					await interiorTextilesLayout(pdfDocument);
				} else {
					await climateScreensLayout(pdfDocument);
				}

				// Download the PDF (uncomment to enable)
				pdfDocument.save(`${product.name}-product-sheet.pdf`);

				// For development purposes only
				// const blob = pdfDocument.output("blob");
				// const url = URL.createObjectURL(blob);
				//
				// // Create a temporary link and click it
				// const link = document.createElement("a");
				// link.href = url;
				// link.target = "_blank";
				// link.rel = "noopener noreferrer";
				// link.style.display = "none";
				// document.body.appendChild(link);
				// link.click();
				// link.remove()
				//
				// // Cleanup
				// setTimeout(() => URL.revokeObjectURL(url), 600_000);

				window.dataLayer.push({
					event: "product_page_download",
				});
			} catch (e) {
				console.error(e);
				alert("PDF generation failed.");
			} finally {
				this.busy = false;
			}
		},
	};
};
export default pdfProductSheet;
